import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import EventReporter, { getEventProviderInstance } from 'appEvents';
import { EventProviders, AppEvents } from 'constants/events';
import { Form, Formik } from 'formik';
import {
  ColorPalettes,
} from 'kinedu-react-components';
import { Button, Texter, colorPalettes } from 'lego-dist';
import KineduAuth from 'kinedu-auth';
import qs from 'querystring';
import { path } from 'ramda';
import * as Yup from 'yup';
import ClassroomsLogo from '../components/shared/ClassroomsLogo';
import { setClientAuthToken } from '../api/ClientInstance';
import { APP_SECTIONS_PATHS } from '../constants/routes';
import { EMULATED_SESSION, ENV, CLIENT_API_URL } from '../config';
import { KEYS_LOCALSTORAGE } from 'constants/general';
import { Underlined } from 'themes/TextInput.theme';

const { KineduPalette } = ColorPalettes;
const { MiscPalette } = colorPalettes;
const { API } = KineduAuth;
const { login, signup } = API(ENV, CLIENT_API_URL);

interface LocalAuthProps {}

const ComponentWrapper = styled.div`
  background-color: ${KineduPalette.newKinedu};
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  form {
    width: 400px;

    .texter {
      margin: 0 0 20px 0px;
      padding: 0;

      .texter-input#auth-email {
        color: white;
        font-size: 24px;
        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          transition: background-color 5000s ease-in-out 0s;
          -webkit-text-fill-color: white !important;
          font-size: 24px !important;
        }
      }

      .texter-label {
        color: white;
      }
    }
  }
`;

const EMULATED_USER = {
  email: EMULATED_SESSION.EMAIL,
  authentication_token: EMULATED_SESSION.AUTH_TOKEN,
  type: 'Teacher',
  locale: '',
  account: {
    name: '',
    type: '',
    kinedu_classrooms_token: { access_token: EMULATED_SESSION.ACCESS_TOKEN },
  },
};

const setLocalData = (user: any, pathToRedirect: string) => {
  const accountToken =
    user.type === 'Teacher'
      ? user.organization.kinedu_classrooms_token.access_token
      : user.account.kinedu_classrooms_token.access_token;
  localStorage.setItem(
    'active_center_token',
    user?.account?.kinedu_classrooms_token?.access_token,
  );
  localStorage.setItem('account_token', accountToken);
  localStorage.setItem('account_type', user.account.type);
  localStorage.setItem('auth_token', user.authentication_token);
  localStorage.setItem('user_id', user.id);
  localStorage.setItem('user_email', user.email);
  localStorage.setItem('user_type', user.type);
  localStorage.setItem('user_confirmed', user.confirmed);
  localStorage.setItem(KEYS_LOCALSTORAGE.USER_LOCALE, user.locale);
  localStorage.setItem(
    KEYS_LOCALSTORAGE.ORGANIZATION_NAME,
    user?.account?.name,
  );
  localStorage.setItem(KEYS_LOCALSTORAGE.ORGANIZATION_ID, user?.account?.id);
  window.location.assign(pathToRedirect);
};

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email')
    .required(),
  password: Yup.string().required(),
});

const LocalAuth: React.FC<LocalAuthProps> = () => {
  const { t, i18n } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);

  const toggleAuth = () => {
    setIsLogin(!isLogin);
    if (isLogin) {
      EventReporter.view(AppEvents.LOGIN_SCREEN(), [
        EventProviders.MIXPANEL,
        EventProviders.GTM,
      ]);
    } else {
      EventReporter.view(AppEvents.SIGNUP_SCREEN(), [
        EventProviders.MIXPANEL,
        EventProviders.GTM,
      ]);
    }
  };

  const handleLogin = ({ email, password }: any) => {
    const loginData = qs.stringify({ email, password });
    login(loginData)
      .then(({ user, data }: any) => {
        user = {
          ...(user || {}),
          ...(data?.user || {}),
        };

        setClientAuthToken(user.authentication_token, user.email);
        // Let's identify the user in MIXPANEL only
        getEventProviderInstance(EventProviders.MIXPANEL).identify(user.id);

        EventReporter.setUserAttributes(user);

        if (user.organization_name) {
          localStorage.setItem('ORGANIZATION_NAME', user.organization_name);
        }

        const firstCenterId = path(['account', 'centers', 0, 'id'], user);

        localStorage.setItem('account_first_center_id', firstCenterId);

        EventReporter.action(AppEvents.LOGIN(), [EventProviders.MIXPANEL]);

        setLocalData(user, APP_SECTIONS_PATHS.DAP);
      })
      .catch((err: any) => {
        console.error(
          '%c🚫 err',
          'background: #a20300; color: #ffa8a6; font-size: 16px',
          err,
        );
      });
  };

  const handleSignup = ({ email, password }: any) => {
    const signupData = {
      user: {
        email,
        password,
        organization_attributes: {
          name: 'Organization name',
        },
      },
    };

    signup(signupData)
      .then(({ data }: any) => {
        const { user } = data;
        // Let's identify the user in MIX PANEL only
        getEventProviderInstance(EventProviders.MIXPANEL).identify(user.id);
        EventReporter.action(AppEvents.SIGNUP(), [
          EventProviders.MIXPANEL,
          EventProviders.GTM,
        ]);
        EventReporter.setUserAttributes(user);

        const accountToken = path(
          ['account', 'kinedu_classrooms_token', 'access_token'],
          user,
        );
        localStorage.setItem('account_token', accountToken);
        localStorage.setItem('auth_token', user.authentication_token);
        localStorage.setItem('user_email', user.email);
        localStorage.setItem('account_first_center_id', user.account.centers[0].id);
        localStorage.setItem('doing_ob', 'true');
        setLocalData(user, APP_SECTIONS_PATHS.ON_BOARDING);
      })
      .catch((err: any) => {
        console.log(
          '%c❕ err',
          'background: #685000; color: #ffe796; font-size: 12px',
          err,
        );
        console.log(
          '%c🚫 err',
          'background: #a20300; color: #ffa8a6; font-size: 16px',
          err?.response?.data,
        );
      });
  };

  const handleEmulatedLogin = (user: any = {}) => {
    setClientAuthToken(user.authentication_token, user.email);
    const firstCenterId = path(['account', 'centers', 0, 'id'], user) || 0;
    localStorage.setItem('account_first_center_id', firstCenterId);
    setLocalData(user, APP_SECTIONS_PATHS.DAP);
  };

  useEffect(() => {
    EventReporter.view(AppEvents.LOGIN_SCREEN(), [EventProviders.MIXPANEL]);
  }, []);

  return (
    <ComponentWrapper>
      <Formik
        initialValues={{
          email: '',
          password: '',
        }}
        validationSchema={loginSchema}
        onSubmit={(res: any) =>
          isLogin ? handleLogin(res) : handleSignup(res)
        }
        render={({ setFieldValue, touched, errors }) => (
          <Form>
            <ClassroomsLogo alt lang={i18n.language} />
            <Texter
              id="auth-email"
              theme={Underlined}
              label={t('Email')}
              placeholder="example@email.com"
              onChange={(e: any) => setFieldValue('email', e)}
              errorMessage={touched.email && errors.email}
            />
            <Texter
              id="auth-password"
              theme={Underlined}
              type="password"
              label={t('Password')}
              placeholder="*****"
              onChange={(e: any) => setFieldValue('password', e)}
              errorMessage={touched.password && errors.password}
            />
            <Button
              type="submit"
              color={isLogin ? 'primary' : 'secondary'}
              rounded
              flat
              fill
            >
              {t(isLogin ? 'Login' : 'Signup')}
            </Button>
            <Button
              style={{ marginTop: '10px' }}
              color="white"
              onClick={toggleAuth}
              fill
            >
              {t(`Change to ${isLogin ? 'Sign up' : 'Login'}`)}
            </Button>
            <Button
              color={MiscPalette.materials}
              onClick={() => handleEmulatedLogin(EMULATED_USER)}
              fill
            >
              {t('Emulate login')}
            </Button>
          </Form>
        )}
      />
    </ComponentWrapper>
  );
};

export default LocalAuth;
